/* eslint-disable @typescript-eslint/no-unused-vars */
import {
  AppBar,
  Button,
  Checkbox,
  Grid,
  Hidden,
  Paper,
  Tab,
  Tabs,
  Typography
} from '@material-ui/core'
import ScopedCssBaseline from '@material-ui/core/ScopedCssBaseline'
import { FC, useEffect, useRef, useState } from 'react'

import {
  getEndDateFromDateObject,
  getStartDateFromDateObject
} from '../../helpers/date'
import { getTranslate } from '../../localization'
import DateTypeSelectorHeader from './DateRangePickerComponents/DateTypeSelectorHeader'
import CalendarMainView from './DateRangePickerComponents/legacy/CalendarMainView'
import DatePickerBox from './DateRangePickerComponents/legacy/DatePickerBox'
import useStyles from './DateRangePickerComponents/styles/DateRangePicker.styles'
import { DateRangePickerProps } from './DateRangePickerComponents/types/DateRangePicker.types'

/**
 * Primary Date Range Picker for both gregorian & jalaali dates
 */
export const DateRangePicker: FC<DateRangePickerProps> = ({
  dateObject,
  showHeader = false,
  numberOfVisibleMonths = 2,
  // withInput = false,
  fixedStart = false,
  fixedEnd = false,
  type = 'gregorian',
  showJalaaliHolidays = false,
  enablePastDays = false,
  enableAllDays = false,
  closeOnDateClicked = false,
  onClose = () => {},
  onChange,
  size = 'big',
  numberOfHoveredItems,
  autoSelectedEndDate,
  showDatePickerBox = true,
  onHeaderClicked = () => {},
  range = true,
  multipleDates = false,
  dateObjectList = [],
  showDateTypeSelectorHeader = false,
  typeOfSelector,
  onChangeTypeOfSelector,
  hideFooterButtons = false,
  hideShadows = false,
  onDoneClicked = (e: any) => {},
  showNumberOfNights = false,
  fromLabel = 'Arrival Date',
  toLabel = 'Departure Date',
  colorOfDoneButton = false,
  endDateCanBeSelectAsStartDate = true,
  showDaysInFooter = false,
  onSelectedWeekDays,
  selectedWeekDays,
  fixHeight,
  disableBefore,
  disableAfter,
  maxRange,
  openAtToday = false
}) => {
  const datePickerPaper = useRef(null)
  const classes = useStyles()
  const [goToday, setGoToday] = useState<boolean>(false)
  const weekDays = [
    'Saturday',
    'Sunday',
    'Monday',
    'Tuesday',
    'Wednesday',
    'Thursday',
    'Friday'
  ]

  const [tempWeekDays, setTempWeekDays] = useState<string[]>(
    selectedWeekDays && selectedWeekDays.length > 0
      ? selectedWeekDays
      : weekDays
  )
  const [selectAllProps, setSelectAllProps] = useState<{
    checked: boolean
    indeterminate: boolean
  }>()
  const [checked, setChecked] = useState<boolean>(false)

  const onGoToTodayClicked = () => {
    setGoToday(!goToday)
  }

  const startDate = getStartDateFromDateObject(dateObject || {}, type)

  const endDate = getEndDateFromDateObject(dateObject || {}, type)

  const showRange = range && !multipleDates

  const addWeekDay = (newWeekDay: string) => {
    const index = tempWeekDays.indexOf(newWeekDay)
    if (index === -1) {
      setTempWeekDays((prevWeekDays) => [...prevWeekDays, newWeekDay])
    } else {
      setTempWeekDays([
        ...tempWeekDays.slice(0, index),
        ...tempWeekDays.slice(index + 1)
      ])
    }
  }

  useEffect(() => {
    onSelectedWeekDays?.(tempWeekDays)
  }, [tempWeekDays])

  const WeekDayBox = (props: { weekDay: string; index: number }) => {
    return (
      <div
        className={[
          classes.weekDayBox,
          selectedWeekDays?.includes(props.weekDay) &&
            classes.weekDayBoxWithBorder
        ].join(' ')}
        onClick={() => addWeekDay(weekDays[props.index])}>
        <Typography variant="caption">
          {getTranslate(`${props.weekDay}`)}
        </Typography>
      </div>
    )
  }

  const handleCheckOnSelectAll = (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    setChecked(event.target.checked)
    if (tempWeekDays.length === weekDays.length) {
      setTempWeekDays([])
    } else {
      setTempWeekDays(weekDays)
    }
  }

  useEffect(() => {
    if (tempWeekDays.length > 0 && tempWeekDays.length < weekDays.length) {
      setSelectAllProps({
        checked: false,
        indeterminate: true
      })
    } else if (tempWeekDays.length === 0) {
      setSelectAllProps({
        checked: false,
        indeterminate: false
      })
    } else if (tempWeekDays.length === weekDays.length) {
      setSelectAllProps({
        checked: true,
        indeterminate: false
      })
    }
  }, [tempWeekDays])

  return (
    <Paper
      style={{ width: numberOfVisibleMonths === 1 ? '448px' : '796px' }}
      className={[
        classes.rootPaper,
        hideShadows ? classes.paperWithoutShadow : '',
        fixHeight ? classes.paperWithScroll : ''
      ].join(' ')}
      ref={datePickerPaper}>
      {showDateTypeSelectorHeader && (
        <DateTypeSelectorHeader
          typeOfSelector={typeOfSelector!}
          onChangeTypeOfSelector={onChangeTypeOfSelector!}
          hideShadows={hideShadows}
        />
      )}
      {showHeader && (
        <Hidden smDown>
          <Paper className={classes.infoBox} onClick={onHeaderClicked}>
            <Grid container>
              <Grid item>
                <DatePickerBox
                  title={fromLabel}
                  selectedDate={startDate}
                  className={classes.pickedDateBox}
                  disableToggle
                  type={type}
                />
              </Grid>
              <Grid item className={classes.checkOutBox}>
                <DatePickerBox
                  title={toLabel}
                  selectedDate={endDate}
                  className={classes.pickedDateBox}
                  disableToggle
                  noBorder
                  type={type}
                />
              </Grid>
              {showNumberOfNights && (
                <Grid item className={classes.nightsCounterGrid}>
                  <Typography
                    variant="body1"
                    className={classes.nightsCounterDigit}>
                    {/* {numberOfNights} */}2
                  </Typography>
                  <Typography
                    variant="body1"
                    className={classes.nightsCounterText}>
                    {getTranslate('Nights')}
                  </Typography>
                </Grid>
              )}
            </Grid>
          </Paper>
        </Hidden>
      )}
      {showDatePickerBox && (
        <>
          <Hidden mdUp>
            <AppBar
              position="relative"
              color="inherit"
              className={classes.datePickerAppBar}>
              <Tabs value={endDate ? 0 : 1}>
                <Tab
                  className={classes.datePickerBoxTab}
                  label={
                    <DatePickerBox
                      title={fromLabel}
                      selectedDate={startDate}
                      className={classes.pickedDateBox}
                      disableToggle
                      noBorder
                      type={type}
                    />
                  }
                />
                <Tab
                  className={classes.datePickerBoxTab}
                  label={
                    <DatePickerBox
                      title={toLabel}
                      disableToggle
                      selectedDate={endDate}
                      className={classes.pickedDateBox}
                      noBorder
                      type={type}
                    />
                  }
                />
              </Tabs>
            </AppBar>
          </Hidden>
          <Grid
            style={{
              width: numberOfVisibleMonths === 1 ? '376px' : '732px'
            }}
            className={classes.calendarMainGrid}>
            <CalendarMainView
              {...{
                startDate,
                endDate,
                fixedStart,
                fixedEnd,
                numberOfVisibleMonths,
                type,
                showJalaaliHolidays,
                enablePastDays,
                enableAllDays,
                onChange,
                size,
                numberOfHoveredItems,
                autoSelectedEndDate,
                multipleDates,
                dateObjectList,
                goToday,
                openAtToday,
                endDateCanBeSelectAsStartDate,
                disableBefore,
                disableAfter,
                maxRange
              }}
              range={showRange}
            />
          </Grid>
          {showDaysInFooter && (
            <div className={classes.daysContainer}>
              <span className={classes.border} />
              <div>
                <div className={classes.dayTitles}>
                  <Typography variant="caption">
                    {getTranslate('Select Week Days')}
                  </Typography>
                  <div>
                    <Checkbox
                      color="primary"
                      checked={checked}
                      onChange={(event) => handleCheckOnSelectAll(event)}
                      {...selectAllProps}
                    />
                    <Typography variant="caption">
                      {getTranslate('Select All')}
                    </Typography>
                  </div>
                </div>
                <div className={classes.weekDaysContainer}>
                  {weekDays.map((weekDay, index) => (
                    <WeekDayBox weekDay={weekDay} index={index} key={index} />
                  ))}
                </div>
              </div>
            </div>
          )}
          {!hideFooterButtons && (
            <Grid
              className={[
                classes.calendarButtonsGrid,
                numberOfVisibleMonths === 1
                  ? classes.calendarButtonsGridForOneMonth
                  : ''
              ].join(' ')}
              container
              direction="row-reverse"
              alignItems="center">
              <Hidden xsDown>
                <Grid item onClick={onDoneClicked}>
                  <Button
                    style={{
                      backgroundColor: colorOfDoneButton
                        ? '#BF0A30'
                        : 'transparent'
                    }}>
                    <Typography
                      style={{
                        color: colorOfDoneButton ? 'white' : '#797979'
                      }}
                      variant="h5"
                      className={classes.calendarGridButton}>
                      {getTranslate('Done')}
                    </Typography>
                  </Button>
                </Grid>
              </Hidden>
              <Hidden xsDown>
                <Grid item onClick={onGoToTodayClicked}>
                  <Button variant="contained" color="primary">
                    <Typography
                      variant="h5"
                      className={classes.GoToTodayButton}>
                      {getTranslate('Go To Today')}
                    </Typography>
                  </Button>
                </Grid>
              </Hidden>
              {/* <Hidden xsDown>
                <Grid item className={classes.calendarResetButtonGrid}>
                  <Typography
                    variant="h5"
                    className={classes.calendarGridButton}>
                    Reset
                  </Typography>
                </Grid>
              </Hidden> */}
              <Hidden smUp>
                <AppBar position="fixed" />
                <Grid item>
                  <Button variant="outlined" onClick={onDoneClicked}>
                    <Typography
                      variant="h5"
                      className={classes.calendarGridButton}>
                      {getTranslate('Done')}
                    </Typography>
                  </Button>
                </Grid>
              </Hidden>
              <Hidden smUp>
                <Grid item className={classes.nightsCounterGridMobile}>
                  <Typography
                    variant="body1"
                    className={classes.nightsCounterDigit}>
                    {numberOfHoveredItems}
                  </Typography>
                </Grid>
              </Hidden>
            </Grid>
          )}
        </>
      )}
    </Paper>
  )
}
